// The grid body for the bulk editor: a paged, sticky-header table with a frozen first column and
// inline cell editing. Rows are PAGED rather than virtualized — at 50–100 rows a plain table needs no
// react-window, and keeps clipboard paste and keyboard navigation simple.

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { FileInput } from '/imports/ui/FileInput';
import { Select } from '/imports/ui/Select';
import {
  ProjectManagementBulkColumn, ProjectManagementBulkRow, projectManagementBulkColId,
  projectManagementFormatValue as formatValue, projectManagementSpineFieldType as spineType,
} from '/imports/api/projectManagement';
import { projectManagementValueInput, projectManagementToStored } from '/imports/ui/ProjectManagementCellEditor';

/** Floor for a dragged column — below this the header controls stop being usable. */
const MIN_COL_WIDTH = 56;
/** The frozen name column's fixed width. */
const NAME_COL_WIDTH = '10em';

/** Compact status pill (the project page's Badge is local to that file). */
const StatusPill = (props: { status?: string }) => (
  <span className={`status-pill ${props.status || 'none'}`}>{props.status || '—'}</span>
);

export const BulkGridStyled = styled.div`
  .status-pill { border-radius: 1rem; padding: 0.05rem 0.45rem; font-size: calc(0.7rem * var(--bulk-font-scale, 1)); background: ${css.grey6}; color: ${css.grey2}; }
  .status-pill.approved { background: ${css.green2}; color: ${css.white}; }
  .status-pill.pending { background: ${css.orange}; color: ${css.white}; }
  .status-pill.rejected { background: ${css.red3}; color: ${css.white}; }
  height: 100%;
  overflow: auto;
  table { border-collapse: separate; border-spacing: 0; font-size: calc(0.82rem * var(--bulk-font-scale, 1)); }
  /* Cells WRAP rather than clip: a column can then be dragged narrower than its content without
     hiding anything, because the intrinsic minimum becomes the longest WORD instead of the longest
     line. Capped at two lines so row heights stay bounded. The clamp lives on the inner text
     carriers, never the td: display -webkit-box on a table cell breaks table layout. */
  th, td { text-align: left; padding: 0.3rem 0.5rem; border-bottom: 1px solid ${css.grey7}; vertical-align: top; }
  td .cell-value, th .col-label, td.frozen .link-btn {
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    overflow-wrap: anywhere;
  }
  /* A HEADING is read, not scanned for a value, and "Produc / tion" is unreadable. It breaks between
     words only; a single word with nowhere to break is clipped by the clamp above instead, and the
     label carries the full text as its title. The project name reads the same way, and its column is
     the one column that is never resizable, so nothing is gained by letting it split mid-word either.
     Every other cell keeps breaking anywhere — that is what lets a column be dragged narrow, and a
     clipped figure is worse than a split one. */
  th .col-label, td.frozen .link-btn { overflow-wrap: normal; word-break: normal; }
  th {
    position: sticky; top: 0; z-index: 2; background: ${css.grey9}; color: ${css.grey2}; font-weight: 600;
    border-bottom: 2px solid ${css.grey6}; font-size: calc(0.72rem * var(--bulk-font-scale, 1)); text-transform: uppercase; letter-spacing: 0.04em;
  }
  /* The project name stays put while the columns scroll sideways. */
  th.frozen, td.frozen {
    position: sticky; left: 0; z-index: 1; background: ${css.white};
    border-right: 1px solid ${css.grey6}; font-weight: 600; overflow: hidden;
  }
  th.frozen { z-index: 3; background: ${css.grey9}; }
  th .col-head { display: flex; align-items: center; gap: 0.2rem; }
  th .col-label { max-width: 12rem; }
  th .col-filter {
    background: none; border: none; cursor: pointer; padding: 0; margin-left: auto; line-height: 0;
    color: ${css.grey4}; opacity: 0.55;
  }
  th:hover .col-filter, th.filtered .col-filter { opacity: 1; }
  th .col-filter svg { width: 0.85rem; height: 0.85rem; min-width: 0.85rem; }
  th.filtered { background: ${css.lightBlue}; }
  th.filtered .col-filter { color: ${css.primaryColor}; }
  /* Sort direction reads off the header itself, matching Table.tsx's arrow idiom. */
  th.sort-asc .col-label::after { content: ' ▲'; font-size: calc(0.6rem * var(--bulk-font-scale, 1)); opacity: 0.7; }
  th.sort-desc .col-label::after { content: ' ▼'; font-size: calc(0.6rem * var(--bulk-font-scale, 1)); opacity: 0.7; }
  tbody tr:hover td { background: ${css.grey9}; }
  tbody tr:hover td.frozen { background: ${css.grey8}; }
  /* A row the current filter no longer matches, held in place until the next refresh. */
  tbody tr.pinned td.frozen { box-shadow: inset 0.15rem 0 0 ${css.green2}; }
  .cell-value { display: block; min-width: 5rem; min-height: 1.2rem; cursor: text; border-radius: 0.2rem; padding: 0.1rem 0.2rem; }
  .cell-value:hover { background: ${css.lightBlue}; }
  .cell-value.empty { color: ${css.grey5}; }
  /* An inline editor must OCCUPY its cell, never resize it. The shared Input and Select are built for
     standalone forms — min-height 3rem and 1rem/2.5rem side padding — which snapped the row ~18px
     taller the moment a cell was opened. */
  /* Height was only half of it. A table sizes a column to its widest content, and an in-flow editor
     contributes its own intrinsic width: ~20 characters for an <input>, the WIDEST OPTION for a
     <select> (39 portfolio names). Measured on open that was +66 to +94px per text column and +51 for
     the picker. Out of flow the editor contributes nothing and simply fills the cell it was opened in,
     which takes every one of those to 0 — min-width: 0 alone did not. */
  .cell-editing { min-width: 0; display: block; position: relative; min-height: 1.4rem; }
  .cell-editing > * { position: absolute; inset: 0; display: block; }
  /* Only the wrapper leaves the flow, so the field fills it — and the wrapper keeps a real box, which
     is what the clear affordance positions against. */
  .cell-editing input, .cell-editing select {
    width: 100%; height: 100%; box-sizing: border-box; min-width: 0; min-height: 0;
    font-family: inherit;
    padding: 0.05rem 1.05rem 0.05rem 0.2rem; line-height: 1.3;
    font-size: calc(0.82rem * var(--bulk-font-scale, 1));
    border: 1px solid ${css.primaryColor}; border-radius: 0.2rem;
  }
  /* The clear affordance comes in with it — at its form-sized offset it would sit over the text. */
  .cell-editing .clear { right: 0.1rem; width: 0.85rem; }
  .cell-editing .clear svg { width: 0.7rem; height: 0.7rem; min-width: 0.7rem; }
  /* Saved and stale states, so a 50-cell paste shows what actually landed. */
  td.saved .cell-value { background: ${css.green3}; }
  td.conflict .cell-value { background: ${css.orange}; color: ${css.white}; }
  .doc-cell { display: flex; align-items: center; gap: 0.4rem; }
  .doc-cell .dl { color: ${css.primaryColor}; line-height: 0; }
  .doc-cell .dl svg { width: 1rem; height: 1rem; min-width: 1rem; }
  /* The stock FileInput is a 121px dashed drop-zone, and it was setting every row's height. In a cell it
     is the project page's task-upload pill instead: one text line tall, scaled with the grid's font. */
  .doc-cell .task-doc-upload { display: inline-flex; width: max-content; }
  .doc-cell .task-doc-upload .target {
    border: none; background: ${css.grey6}; color: ${css.grey2}; border-radius: 1rem;
    padding: 0.2rem 0.55rem; display: inline-flex; align-items: center; gap: 0.3rem;
    font-size: calc(0.74rem * var(--bulk-font-scale, 1)); line-height: 1; cursor: pointer; white-space: nowrap;
  }
  .doc-cell .task-doc-upload .target:hover { background: ${css.grey5}; color: ${css.grey1}; }
  .doc-cell .task-doc-upload.dragging .target { background: ${css.lightBlue}; color: ${css.grey1}; outline: 2px dashed ${css.primaryColor}; outline-offset: 1px; }
  .doc-cell .task-doc-upload .target > div { line-height: 1; }
  .doc-cell .task-doc-upload .target .icon { margin: 0; }
  .doc-cell .task-doc-upload .target .icon svg { width: 0.85rem; height: 0.85rem; min-width: 0.85rem; }
  .col-group { color: ${css.grey4}; font-weight: normal; text-transform: none; letter-spacing: 0; }
  /* Grip sits on the cell's trailing edge. The th is already position: sticky (above), which is a
     positioned value, so it is the containing block for this absolute child. */
  th .col-resize {
    position: absolute; top: 0; right: 0; width: 0.5rem; height: 100%; cursor: col-resize;
    user-select: none; touch-action: none;
  }
  th .col-resize::after {
    content: ''; position: absolute; top: 20%; right: 0.18rem; width: 1px; height: 60%;
    background: ${css.grey6};
  }
  th:hover .col-resize::after, th .col-resize.dragging::after { background: ${css.primaryColor}; width: 2px; }
`;

export interface BulkGridProps {
  columns: ProjectManagementBulkColumn[];
  rows: ProjectManagementBulkRow[];
  columnLabel: (c: ProjectManagementBulkColumn) => string;
  // Options for every spine column that is a REFERENCE (portfolioId, ownerId, epcId): keyed by
  // column key, so the cell renders a name and edits through a picker.
  referenceOptions: { [key: string]: { value: string, label: string }[] };
  saved: Set<string>;                 // `${projectId}:${colId}` cells written by the last batch
  conflicts: Set<string>;
  pinned: Set<string>;                // projectIds kept visible after an edit dropped them from the filter
  filteredColIds: Set<string>;
  sort?: { colId: string, dir: 1 | -1 };
  colWidths: Record<string, number>;               // colId -> px; absent = size to content
  onResizeColumn: (colId: string, width?: number) => void;   // undefined width = back to auto
  onCommit: (row: ProjectManagementBulkRow, column: ProjectManagementBulkColumn, value: any) => void;
  onUploadDocument: (row: ProjectManagementBulkRow, docType: string, file: any) => void;
  onPaste: (row: ProjectManagementBulkRow, column: ProjectManagementBulkColumn, text: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenFilter: (column: ProjectManagementBulkColumn, trigger: HTMLElement) => void;
}

export const ProjectManagementBulkGrid = (props: BulkGridProps) => {
  const {
    columns, rows, columnLabel, referenceOptions, saved, conflicts, pinned,
    onCommit, onUploadDocument, onPaste, onOpenProject, onOpenFilter, filteredColIds, sort,
    colWidths, onResizeColumn,
  } = props;
  const [editing, setEditing] = useState<{ rowId: string, colId: string, draft: any }>();
  // The blur handler must read the LATEST keystroke, not the draft captured when it last rendered —
  // React can batch a fast type-then-tab so that the closure is a render behind.
  const draftRef = useRef<any>(undefined);

  const startEdit = (rowId: string, colId: string, value: any) => {
    draftRef.current = value;
    setEditing({ rowId, colId, draft: value });
  };
  const editDraft = (rowId: string, colId: string, v: any) => {
    draftRef.current = v;
    setEditing({ rowId, colId, draft: v });
  };
  const [dragging, setDragging] = useState<string>();
  /**
   * Drag one column's trailing edge. Widths are applied through a <colgroup>, so the table's own
   * layout algorithm does the work and neighbouring columns are left alone.
   * @param colId - the column being sized
   * @param e - the pointerdown that started the drag
   */
  const startResize = (colId: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();                                   // never open the filter panel on a grip drag
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLElement | null;
    const startX = e.clientX;
    const startWidth = th ? th.getBoundingClientRect().width : MIN_COL_WIDTH;
    setDragging(colId);
    const move = (ev: PointerEvent) => onResizeColumn(colId, Math.max(MIN_COL_WIDTH, startWidth + (ev.clientX - startX)));
    const up = () => {
      setDragging(undefined);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const commit = (row: ProjectManagementBulkRow, column: ProjectManagementBulkColumn, raw: any, type?: any) => {
    setEditing(undefined);
    const stored = projectManagementToStored(type, raw);
    const cell = row.cells[projectManagementBulkColId(column)];
    const was = (cell?.value as any) ?? null;
    const now = stored ?? null;
    // Dates go round the widget as strings and come back as new Date objects, so === is never true for
    // them: without comparing the instants, merely opening a date cell and clicking away would rewrite
    // the same value and log a history entry for a field nobody touched.
    const same = was instanceof Date && now instanceof Date ? was.getTime() === now.getTime() : was === now;
    if (same) return;
    onCommit(row, column, stored ?? null);
  };

  return (
    <BulkGridStyled>
      <table>
        {/* Widths live on <col> rather than on each th/td: one element per column, and the table
            honours it as a suggestion the wrap-enabled cells can actually satisfy. */}
        <colgroup>
          {/* The name column is FIXED, never resizable — it is the sticky spine every other column
              scrolls past, so a drifting width would shift the whole grid. In em, so it tracks the
              text-size setting and keeps roughly the same character count at any scale. */}
          <col style={{ width: NAME_COL_WIDTH }} />
          {columns.map((c) => {
            const w = colWidths[projectManagementBulkColId(c)];
            return <col key={projectManagementBulkColId(c)} style={w ? { width: `${w}px` } : undefined} />;
          })}
        </colgroup>
        <thead>
          <tr>
            <th className="frozen">{strings.Name()}</th>
            {
              columns.map((c) => {
                const colId = projectManagementBulkColId(c);
                const sorted = sort?.colId === colId ? (sort.dir === 1 ? ' sort-asc' : ' sort-desc') : '';
                return (
                  <th key={colId} className={`${filteredColIds.has(colId) ? 'filtered' : ''}${sorted}`}>
                    <span className="col-head">
                      <span className="col-label" title={columnLabel(c)}>{columnLabel(c)}</span>
                      <button
                        className="col-filter"
                        title={strings.Filter()}
                        // Anchor on the heading cell, not the button: the panel then drops from the
                        // column's own left edge and clears the (padded) header row instead of overlapping it.
                        onClick={(e) => onOpenFilter(c, e.currentTarget.closest('th') || e.currentTarget)}
                      >
                        <Icon icon={filteredColIds.has(colId) ? 'filterAlt' : 'expandMore'} />
                      </button>
                    </span>
                    <span
                      className={`col-resize${dragging === colId ? ' dragging' : ''}`}
                      onPointerDown={(e) => startResize(colId, e)}
                      // Double-click releases the column back to sizing itself from its content.
                      onDoubleClick={() => onResizeColumn(colId, undefined)}
                      title={strings.DragToResize()}
                    />
                  </th>
                );
              })
            }
          </tr>
        </thead>
        <tbody>
          {
            rows.map((row) => (
              <tr key={row.projectId} className={pinned.has(row.projectId) ? 'pinned' : undefined} title={pinned.has(row.projectId) ? strings.RowsKeptAfterEdit() : undefined}>
                <td className="frozen" title={row.name}>
                  <button className="link-btn" onClick={() => onOpenProject(row.projectId)}>{row.name}</button>
                </td>
                {
                  columns.map((c) => {
                    const colId = projectManagementBulkColId(c);
                    const cell = row.cells[colId] || {};
                    const cellKey = `${row.projectId}:${colId}`;
                    const isEditing = editing?.rowId === row.projectId && editing?.colId === colId;
                    const type = c.kind === 'parameter' ? cell.type : spineType(c.key);
                    const cls = `${saved.has(cellKey) ? 'saved' : ''}${conflicts.has(cellKey) ? ' conflict' : ''}`;
                    if (c.kind === 'document') {
                      return (
                        <td key={colId} className={cls}>
                          <span className="doc-cell">
                            <StatusPill status={cell.status} />
                            <FileInput className="task-doc-upload" placeholder={strings.Upload()} onChange={(file) => onUploadDocument(row, c.key, file)} />
                            {cell.fileUrl && <a className="dl" href={cell.fileUrl} target="_blank" rel="noreferrer"><Icon icon="docs" /></a>}
                          </span>
                        </td>
                      );
                    }
                    // A reference column is a picker, but it reads as a value like any other cell until
                    // you go to change it — rendering the <select> permanently kept the column in edit
                    // mode, so it never wrapped or clamped, and its control set every row's height.
                    const refOptions = c.kind === 'spine' ? referenceOptions[c.key] : undefined;
                    if (refOptions) {
                      const portfolioLabel = refOptions.find((o) => o.value === cell.value)?.label;
                      return (
                        <td key={colId} className={cls} title={conflicts.has(cellKey) ? strings.EditedElsewhere() : undefined}>
                          {
                            isEditing ?
                              <span
                                className="cell-editing"
                                // Select takes no autoFocus prop, so focus the native node once it mounts.
                                ref={(el) => el?.querySelector('select')?.focus()}
                                onKeyDown={(e) => { if (e.key === 'Escape') setEditing(undefined); }}
                              >
                                <Select
                                  value={(cell.value as string) || ''}
                                  options={[{ value: '', label: '—' }, ...refOptions]}
                                  onChange={(v) => { setEditing(undefined); onCommit(row, c, v || null); }}
                                />
                              </span>
                              :
                              <span
                                className={`cell-value${portfolioLabel ? '' : ' empty'}`}
                                onClick={() => startEdit(row.projectId, colId, cell.value)}
                              >
                                {portfolioLabel || '—'}
                              </span>
                          }
                        </td>
                      );
                    }
                    return (
                      <td
                        key={colId}
                        className={cls}
                        title={conflicts.has(cellKey) ? strings.EditedElsewhere() : undefined}
                        onPaste={(e) => {
                          const text = e.clipboardData.getData('text/plain');
                          if (!text.includes('\t') && !text.includes('\n')) return;  // single cell → normal paste
                          e.preventDefault();
                          onPaste(row, c, text);
                        }}
                      >
                        {
                          isEditing ?
                            <span
                              className="cell-editing"
                              // A boolean cell edits through a Select, which takes no autoFocus prop. Nothing
                              // below works unfocused: Enter and Escape are read here, and blur-to-commit can
                              // only fire once something has focus.
                              ref={(el) => el?.querySelector('select')?.focus()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); commit(row, c, draftRef.current, type); }
                                if (e.key === 'Escape') setEditing(undefined);
                              }}
                            >
                              {projectManagementValueInput(type as any, editing.draft, (v) => editDraft(row.projectId, colId, v), {
                                autoFocus: true,
                                onBlur: () => commit(row, c, draftRef.current, type),
                              })}
                            </span>
                            :
                            <span
                              className={`cell-value${cell.value == null || cell.value === '' ? ' empty' : ''}`}
                              onClick={() => startEdit(row.projectId, colId, cell.value)}
                            >
                              {c.kind === 'parameter' ? formatValue(cell.value, cell.unit, cell.type) : (cell.value == null ? '—' : String(cell.value))}
                            </span>
                        }
                      </td>
                    );
                  })
                }
              </tr>
            ))
          }
          {!rows.length && <tr><td className="frozen">—</td>{columns.map((c) => <td key={projectManagementBulkColId(c)} />)}</tr>}
        </tbody>
      </table>
    </BulkGridStyled>
  );
};
